import { queryOptions } from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import type {
  ChartResolution,
  ChartSearchParams,
  HistoryWindow,
  PaneRead,
  PriceBasis,
} from '@/features/trading-chart/api/chartApi'
import { plainSymbol } from '@/features/trading-chart/api/chartMappers'
import { chartService } from '@/features/trading-chart/api/chartService'
import { chartKeys } from '@/features/trading-chart/api/keys'
import { keepWarmConfig, keepWarmSymbol } from '@/features/trading-chart/lib/warmCache'

/** How often the forming bar is re-read while NEPSE trades (owner, 2026-10-30: 10s). */
export const LIVE_BAR_POLL_MS = 10_000

const SYMBOL_FRESH_MS = 60 * 60_000
const SEARCH_FRESH_MS = 60_000
const HISTORY_HEAD_FRESH_MS = 60_000

/**
 * Study panes are built each evening (services/chart/studies.py), so a session's values do not
 * move while NEPSE trades; half an hour only picks up the evening's build on a page left open.
 */
const PANE_FRESH_MS = 30 * 60_000

/**
 * Sessions the average trade size pane reads: the same window the study panes get from mm-be by
 * default, so the panes start on the same session.
 */
const TRADE_SIZE_SESSIONS = 250

/**
 * The fewest bars a symbol's first `getBars` is answered with. The library's
 * first request covers about the visible range (300 daily bars), and it then
 * asked twice more, each waiting on the one before, to fill the screen and
 * the scroll-back margin. A thousand (four years of NEPSE sessions, about
 * 60 kB) answers all three at once: the library keeps the older bars and asks
 * again only when the member scrolls past them.
 */
export const FIRST_REQUEST_BARS = 1000

/**
 * docs/route-loading.md, "Not-found answers": an unknown symbol is an answer,
 * not a failure, so the chart page's loader settles on `null` and renders its
 * not-found branch instead of the route error page. Any other error throws.
 */
async function nullOnSymbolNotFound<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read()
  } catch (error) {
    if (hasCode(error, 'SYMBOL_NOT_FOUND')) return null
    throw error
  }
}

/**
 * The datafeed's `onReady` config. It changes only with a deploy, so it is
 * read once per session. Each answer is also kept for the next visit
 * (lib/warmCache.ts).
 */
export const chartConfigQueryOptions = () =>
  queryOptions({
    queryKey: chartKeys.config(),
    queryFn: async () => {
      const config = await chartService.config()
      keepWarmConfig(config)
      return config
    },
    staleTime: Infinity,
    gcTime: Infinity,
  })

/**
 * One symbol's info, for the page loader and `resolveSymbol` alike, so the
 * loader's read is the one the chart then resolves from. `null` means mm-be
 * has no such symbol. Kept for an hour: a listing's name, sector and tick size
 * do not change within one. Each answer also updates the next visit's copy
 * (lib/warmCache.ts), and a `null` drops it.
 */
export const chartSymbolQueryOptions = (symbol: string) => {
  const plain = plainSymbol(symbol)
  return queryOptions({
    queryKey: chartKeys.symbol(plain),
    queryFn: async () => {
      const info = await nullOnSymbolNotFound(() => chartService.symbol(plain))
      keepWarmSymbol(plain, info)
      return info
    },
    staleTime: SYMBOL_FRESH_MS,
    gcTime: SYMBOL_FRESH_MS,
  })
}

/** One query in the chart's symbol search. A minute is long enough to cover a retyped word. */
export const chartSearchQueryOptions = (params: ChartSearchParams) =>
  queryOptions({
    queryKey: chartKeys.search(params),
    queryFn: () => chartService.search(params),
    staleTime: SEARCH_FRESH_MS,
  })

/**
 * One `getBars` window, always read fresh and never kept: the library holds
 * the bars it has drawn, so a cached copy would only be a second 5,000-bar
 * array in memory.
 */
export const chartHistoryQueryOptions = (
  symbol: string,
  resolution: ChartResolution,
  window: HistoryWindow,
  basis: PriceBasis = 'adjusted',
) =>
  queryOptions({
    queryKey: chartKeys.history(symbol, resolution, window, basis),
    queryFn: () => chartService.history(symbol, window, resolution, basis),
    staleTime: 0,
    gcTime: 0,
  })

/**
 * A symbol's newest `FIRST_REQUEST_BARS` bars, up to today. The page loader
 * asks for them while the library is still starting, and the datafeed's first
 * `getBars` answers from them (lib/datafeed.ts), so the first candles wait on
 * no request of their own. Kept a minute: long enough to be read once, and a
 * Retry inside it reads the same bars. After that the library holds what it
 * drew.
 */
export const chartHistoryHeadQueryOptions = (symbol: string, basis: PriceBasis = 'adjusted') => {
  const plain = plainSymbol(symbol)
  return queryOptions({
    queryKey: chartKeys.historyHead(plain, basis),
    queryFn: () => chartService.history(plain, { countback: FIRST_REQUEST_BARS }, undefined, basis),
    staleTime: HISTORY_HEAD_FRESH_MS,
    gcTime: HISTORY_HEAD_FRESH_MS,
  })
}

/**
 * One chart pane's series: a study, or the average trade size. The upper-cased symbol is the key,
 * so a stock chart and an index chart asking for the market's breadth (always `NEPSE`) share one
 * entry.
 */
export const chartPaneQueryOptions = (read: PaneRead, symbol: string) => {
  const plain = plainSymbol(symbol)
  return queryOptions({
    queryKey: chartKeys.pane(read, plain),
    queryFn: () =>
      read.kind === 'study'
        ? chartService.study(read.study, plain, read.params)
        : chartService.tradeSize(plain, TRADE_SIZE_SESSIONS),
    staleTime: PANE_FRESH_MS,
  })
}

/** The server clock, read whenever the library syncs to it. */
export const serverTimeQueryOptions = () =>
  queryOptions({
    queryKey: chartKeys.time(),
    queryFn: () => chartService.serverTime(),
    staleTime: 0,
    gcTime: 0,
  })

/**
 * The newest bars at one resolution (`chartService.latestBars`), polled while NEPSE trades.
 *
 * The poll rules, all of them:
 *   - polls every `LIVE_BAR_POLL_MS` only while `isOpen`; closed or unknown
 *     (`marketStatusQueryOptions` not yet read) is no request at all;
 *   - never while the tab is hidden: React Query skips interval ticks in the
 *     background, and resumes on the next tick once the tab is visible again;
 *   - pauses offline, as every query does, and resumes on reconnect.
 * `isOpen` comes from `liveBar.ts`, which watches the market status and makes
 * the final read when the market closes.
 */
export const liveBarQueryOptions = (
  symbol: string,
  resolution: ChartResolution,
  isOpen: boolean,
  basis: PriceBasis = 'adjusted',
) =>
  queryOptions({
    queryKey: chartKeys.liveBar(symbol, resolution, basis),
    queryFn: () => chartService.latestBars(symbol, resolution, basis),
    enabled: isOpen,
    refetchInterval: isOpen ? LIVE_BAR_POLL_MS : false,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

/** My saved layouts, newest first. A save or delete invalidates it. */
export const chartLayoutsQueryOptions = () =>
  queryOptions({
    queryKey: chartKeys.layouts(),
    queryFn: () => chartService.layouts(),
  })

/** One layout's content, read fresh on every load and not kept: it can run to a megabyte. */
export const chartLayoutContentQueryOptions = (layoutId: number) =>
  queryOptions({
    queryKey: chartKeys.layout(layoutId),
    queryFn: () => chartService.layoutContent(layoutId),
    staleTime: 0,
    gcTime: 0,
  })

/** My indicator template names, A→Z. A save or delete invalidates it. */
export const studyTemplatesQueryOptions = () =>
  queryOptions({
    queryKey: chartKeys.studyTemplates(),
    queryFn: () => chartService.studyTemplates(),
  })

/** One template's content, read fresh on every apply and not kept. */
export const studyTemplateContentQueryOptions = (name: string) =>
  queryOptions({
    queryKey: chartKeys.studyTemplate(name),
    queryFn: () => chartService.studyTemplateContent(name),
    staleTime: 0,
    gcTime: 0,
  })
